import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../../api/apiClient";
import { fetchDocument } from "../../api/documentDownload";
import Button from "../../ui/Button";
import ModalA11yWrapper from "../../ui/ModalA11yWrapper";

const pdfNamePattern = /\.pdf$/i;
const wordNamePattern = /\.(doc|docx)$/i;

const formatBytes = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const isPdfDocument = (filename, mimeType) =>
  String(mimeType || "").toLowerCase() === "application/pdf" || pdfNamePattern.test(String(filename || ""));

const isWordDocument = (filename, mimeType) => {
  const normalizedMime = String(mimeType || "").toLowerCase();
  return (
    normalizedMime === "application/msword" ||
    normalizedMime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    wordNamePattern.test(String(filename || ""))
  );
};

const resolveAbsoluteUrl = (url) => {
  if (!url || url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
  const base = apiClient.defaults.baseURL || "";
  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  if (normalizedBase.startsWith("http")) {
    return `${normalizedBase}${url.startsWith("/") ? url : `/${url}`}`;
  }
  return `${window.location.origin}${normalizedBase}${url.startsWith("/") ? url : `/${url}`}`;
};

export default function PolicyDocumentModal({
  open,
  onClose,
  documentName,
  documentMimeType,
  documentSizeBytes,
  documentUrl,
  versionLabel,
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const absoluteUrl = useMemo(() => resolveAbsoluteUrl(documentUrl), [documentUrl]);
  const isPdf = useMemo(() => isPdfDocument(documentName, documentMimeType), [documentName, documentMimeType]);
  const isWord = useMemo(() => isWordDocument(documentName, documentMimeType), [documentName, documentMimeType]);
  const titleId = "policy-document-modal-title";

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    setPreviewUrl(null);
    setPreviewError("");

    if (!open || !absoluteUrl) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    fetchDocument(absoluteUrl, { allowLocalPreview: true })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Preview not available");
        }
        const blob = await response.blob();
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (active) {
          setPreviewError("Preview not available. Open or download the document instead.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [absoluteUrl, open]);

  if (!open) return null;

  return (
    <ModalA11yWrapper
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      backdropStyle={{ background: "rgba(15, 23, 42, 0.42)", zIndex: 3000 }}
      containerStyle={{
        width: "min(94vw, 1120px)",
        height: "min(92vh, 860px)",
        padding: 0,
        overflow: "hidden",
        borderRadius: 18,
      }}
    >
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr", height: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            padding: "18px 22px",
            borderBottom: "1px solid rgba(148, 163, 184, 0.28)",
            background:
              "linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(239,246,255,0.98) 52%, rgba(224,231,255,0.98) 100%)",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#64748b",
                marginBottom: 6,
              }}
            >
              Policy document {versionLabel ? `| ${versionLabel}` : ""}
            </div>
            <div id={titleId} style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", wordBreak: "break-word" }}>
              {documentName || "Policy document"}
            </div>
            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {documentMimeType && (
                <span
                  style={{
                    padding: "5px 10px",
                    borderRadius: 999,
                    background: "rgba(15, 23, 42, 0.06)",
                    color: "#334155",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {documentMimeType}
                </span>
              )}
              {documentSizeBytes ? (
                <span
                  style={{
                    padding: "5px 10px",
                    borderRadius: 999,
                    background: "rgba(37, 99, 235, 0.08)",
                    color: "#1d4ed8",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {formatBytes(documentSizeBytes)}
                </span>
              ) : null}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {previewUrl && (
              <a
                href={previewUrl}
                download={documentName || "policy-document"}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 40,
                  padding: "0 14px",
                  borderRadius: 10,
                  border: "1px solid #cbd5e1",
                  color: "#0f172a",
                  textDecoration: "none",
                  fontWeight: 600,
                  background: "#ffffff",
                }}
              >
                Download
              </a>
            )}
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        <div style={{ background: "#e2e8f0", padding: 18, overflow: "auto" }}>
          <div
            style={{
              height: "100%",
              minHeight: 480,
              borderRadius: 16,
              background: "#ffffff",
              boxShadow: "0 24px 48px rgba(15, 23, 42, 0.12)",
              overflow: "hidden",
              border: "1px solid rgba(148, 163, 184, 0.25)",
            }}
          >
            {loading && <div style={{ padding: 24, color: "#475569" }}>Loading document preview...</div>}
            {!loading && previewError && <div style={{ padding: 24, color: "#b91c1c" }}>{previewError}</div>}
            {!loading && !previewError && isPdf && previewUrl && (
              <iframe
                title={documentName || "Policy document"}
                src={previewUrl}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            )}
            {!loading && !previewError && !isPdf && (
              <div
                style={{
                  display: "grid",
                  placeItems: "center",
                  minHeight: "100%",
                  padding: 32,
                  background:
                    "radial-gradient(circle at top, rgba(191,219,254,0.28), transparent 42%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                }}
              >
                <div style={{ maxWidth: 520, textAlign: "center", display: "grid", gap: 14 }}>
                  <div style={{ fontSize: 54 }}>{isWord ? "DOC" : "FILE"}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
                    Inline preview is available for PDFs
                  </div>
                  <div style={{ color: "#475569", lineHeight: 1.6 }}>
                    This document has been uploaded successfully and can be downloaded from this window.
                  </div>
                  {previewUrl && (
                    <div>
                      <a
                        href={previewUrl}
                        download={documentName || "policy-document"}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: 44,
                          padding: "0 18px",
                          borderRadius: 10,
                          background: "#0f172a",
                          color: "#ffffff",
                          textDecoration: "none",
                          fontWeight: 700,
                        }}
                      >
                        Download document
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!loading && !previewError && !previewUrl && (
              <div style={{ padding: 24, color: "#475569" }}>Preview not available.</div>
            )}
          </div>
        </div>
      </div>
    </ModalA11yWrapper>
  );
}
